import { getBackendUrl } from "@/lib/backend";
import { renderImageUrl } from "@/lib/imageUrl";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import axios, { API } from "@/lib/api";
import { formatApiError } from "@/lib/apiError";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";
import {
  LogOut, Wand2, Loader2,
  Image as ImageIcon, Download, Layers3, Edit3, ClipboardList, Box, BookMarked, Ticket, Plus, Gem, Sparkles, UploadCloud, Undo2, Camera, ScanSearch,
} from "lucide-react";
import LuxuryConfirmModal from "@/components/LuxuryConfirmModal";
import RefineDialog from "@/components/RefineDialog";
import QuickRefineDialog from "@/components/QuickRefineDialog";
import SplitComponentDialog from "@/components/SplitComponentDialog";
import MaterialsDrawer from "@/components/MaterialsDrawer";
import RendersChip from "@/components/RendersChip";
import SaveToPortfolioDialog from "@/components/SaveToPortfolioDialog";
import PortfolioPanel from "@/components/PortfolioPanel";
import RedeemCodeDialog from "@/components/RedeemCodeDialog";
import CheckoutDialog from "@/components/CheckoutDialog";
import PackPickerDialog from "@/components/PackPickerDialog";
import CustomTopUpDialog from "@/components/CustomTopUpDialog";
import MaterialPresetPicker from "@/components/MaterialPresetPicker";
import TeamHeaderChip from "@/components/TeamHeaderChip";
import RefSlot from "@/components/RefSlot";
import StudioGallery from "@/components/StudioGallery";
import CadDialog from "@/components/CadDialog";
import CameraViewsDialog from "@/components/CameraViewsDialog";
import RenderFromCadDialog from "@/components/RenderFromCadDialog";
import AutoSplitDialog from "@/components/AutoSplitDialog";

const STYLES = [
  "Jadau", "Polki", "Meenakari", "Temple", "Idol", "Kundan",
  "Diamond", "Antique", "Rajwadi", "Bridal", "Lightweight", "Modern Fusion",
];

// Render-style metal palette. The `id` is what the backend sees so the
// prompt stays parseable; the `label` is user-facing. Default is empty
// (the brief decides) — designers opt-in.
const RENDER_METALS = [
  { id: "yellow_gold", label: "Yellow Gold" },
  { id: "rose_gold",   label: "Rose Gold" },
  { id: "white_gold",  label: "White Gold" },
  { id: "platinum",    label: "Platinum" },
];

const VIEWS = ["front", "left", "back", "right"];

// Labels for camera-view tabs (matches backend `_angle_to_view_id`). Any
// view starting with "cam_" gets a tab; custom angles are decoded inline.
const CAM_VIEW_LABELS = {
  cam_topdown:  "Top-down",
  cam_34front:  "3/4 Front",
  cam_profile:  "Side view",
  cam_tilt45:   "45° Tilt",
  cam_hero:     "Hero",
};
const formatViewLabel = (v) => {
  if (!v) return "";
  if (CAM_VIEW_LABELS[v]) return CAM_VIEW_LABELS[v];
  if (v.startsWith("cam_custom_")) {
    const [, , az, el] = v.split("_");
    return `Custom ${az}° / ${el}°`;
  }
  // Legacy L/B/R views — keep capitalised.
  return v.charAt(0).toUpperCase() + v.slice(1);
};

const BACKEND = getBackendUrl();

export default function UserDashboard() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();

  // Back-button trap after Google OAuth landing. AuthCallback.jsx sets
  // `facetra_post_oauth_landing` in sessionStorage right before redirecting
  // here. If present, we push a duplicate /app history entry so the user's
  // first back-press stays on /app instead of re-running the consumed
  // Emergent OAuth callback URL (which would show "Invalid state parameter").
  useEffect(() => {
    try {
      if (sessionStorage.getItem("facetra_post_oauth_landing") !== "1") return;
      sessionStorage.removeItem("facetra_post_oauth_landing");
    } catch { return; }
    // Push a duplicate entry — first back-press lands us here again instead
    // of demobackend.emergentagent.com/.../oauth/callback.
    window.history.pushState({ facetra_trap: true }, "", window.location.href);
    const onPop = (e) => {
      // After the first back-press consumed the trap entry, only re-trap
      // if the user is still on /app. Else (they navigated elsewhere) let
      // popstate flow normally.
      if (window.location.pathname === "/app") {
        window.history.pushState({ facetra_trap: true }, "", window.location.href);
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState("Jadau");
  const [metal, setMetal] = useState(""); // render-style metal colour (optional)
  const [variants, setVariants] = useState(1);
  const [refFile, setRefFile] = useState(null); // primary sketch/design reference
  const [refPreview, setRefPreview] = useState(null);
  const [gemFile, setGemFile] = useState(null); // gemstone / material reference
  const [gemPreview, setGemPreview] = useState(null);
  const [texFile, setTexFile] = useState(null); // texture / colour reference
  const [texPreview, setTexPreview] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [multiviewing, setMultiviewing] = useState(false);
  const [cameraViewsOpen, setCameraViewsOpen] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [activeView, setActiveView] = useState("front");
  const [gallery, setGallery] = useState([]);
  const [galleryNextCursor, setGalleryNextCursor] = useState(null);
  const [galleryHasMore, setGalleryHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [quota, setQuota] = useState({ used: 0, limit: 10, remaining: 10 });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [refineTarget, setRefineTarget] = useState(null);
  const [materialsTarget, setMaterialsTarget] = useState(null);
  const [presetPickerTarget, setPresetPickerTarget] = useState(null);
  const [cadDialogOpen, setCadDialogOpen] = useState(false);
  const [renderFromCadOpen, setRenderFromCadOpen] = useState(false);
  const [cadReadyLoading, setCadReadyLoading] = useState(false);
  const [loadingGallery, setLoadingGallery] = useState(true);
  const [downloadUrl, setDownloadUrl] = useState("");
  const [savePortfolioTarget, setSavePortfolioTarget] = useState(null);
  const [activeTab, setActiveTab] = useState("studio"); // "studio" | "portfolio"
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [buyPack, setBuyPack] = useState(null);
  const [packPickerOpen, setPackPickerOpen] = useState(false);
  const [customTopUpOpen, setCustomTopUpOpen] = useState(false);
  const [quickRefineTarget, setQuickRefineTarget] = useState(null);
  // Split-component dialog target (Option A — prompt-based isolation on
  // matte white). Kept as its own state so it never intersects with the
  // working refine flow above.
  const [splitTarget, setSplitTarget] = useState(null);
  // Auto-split (Component Splitter Automation) — self-contained, does
  // not share state with manual splitTarget above.
  const [autoSplitTarget, setAutoSplitTarget] = useState(null);
  const [packs, setPacks] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/billing/packs`)
      .then((r) => setPacks(r.data?.packs || []))
      .catch((err) => {
        console.warn("loading billing packs failed", err);
      });
  }, []);

  // Prefetch the 3D viewer bundle (model-viewer + three.js, ~600KB)
  // the moment the studio mounts. By the time the user clicks the CAD
  // button and a job finishes, the bundle is already in module cache so
  // <model-viewer> renders the mesh immediately instead of cold-starting.
  // `requestIdleCallback` keeps it off the critical path; we fall back
  // to a small setTimeout for Safari.
  useEffect(() => {
    const warm = () => {
      import("@google/model-viewer").catch(() => { /* offline / blocked */ });
    };
    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      const id = window.requestIdleCallback(warm, { timeout: 4000 });
      return () => window.cancelIdleCallback?.(id);
    }
    const t = setTimeout(warm, 2500);
    return () => clearTimeout(t);
  }, []);

  const refreshWallet = async () => {
    try {
      const w = await axios.get(`${API}/studio/wallet`);
      setQuota(w.data);
    } catch (err) {
      console.warn("refreshWallet failed", err);
    }
  };
  // The Emergent preview iframe doesn't carry `allow-downloads` in its sandbox,
  // so browsers silently swallow file downloads. We detect that here so the
  // PNG button can offer a one-click "open in a real tab" escape hatch.
  const [inIframe, setInIframe] = useState(false);
  useEffect(() => {
    try { setInIframe(window.self !== window.top); } catch { setInIframe(true); }
  }, []);

  useEffect(() => {
    if (!authLoading && !user) navigate("/signin", { replace: true });
  }, [user, authLoading, navigate]);

  const loadGallery = useCallback(async () => {
    setLoadingGallery(true);
    try {
      const [g, q] = await Promise.all([
        axios.get(`${API}/studio/generations?limit=40`),
        axios.get(`${API}/studio/wallet`),
      ]);
      setGallery(g.data.entries || []);
      setGalleryNextCursor(g.data.next_cursor || null);
      setGalleryHasMore(!!g.data.has_more);
      setQuota(q.data);
    } catch (err) {
      if (err?.response?.status === 401) navigate("/signin", { replace: true });
    } finally {
      setLoadingGallery(false);
    }
  }, [navigate]);

  // Auto-pick the latest front render the FIRST time the gallery loads.
  // Kept out of `loadGallery` deliberately — when it was inline, changing
  // `currentId` (via clicking a gallery tile) caused useCallback to
  // re-create, which caused the parent useEffect to refire, which wiped
  // any pages already loaded via "Load older renders". See iteration_19
  // RCA comment #4.
  const autoPickedRef = useRef(false);
  useEffect(() => {
    if (autoPickedRef.current) return;
    if (gallery.length === 0) return;
    if (currentId !== null) { autoPickedRef.current = true; return; }
    const front = gallery.find((e) => (e.view || "front") === "front");
    if (front) {
      setCurrentId(front.id);
      autoPickedRef.current = true;
    }
  }, [gallery, currentId]);

  /** Load the next page of older renders using the keyset cursor.
   *  Append-only — never re-fetches what's already in state. */
  const loadMoreGallery = useCallback(async () => {
    if (!galleryNextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const r = await axios.get(
        `${API}/studio/generations?limit=40&cursor=${encodeURIComponent(galleryNextCursor)}`,
      );
      const incoming = r.data.entries || [];
      setGallery((prev) => {
        // Defensive dedupe by id — keyset can never overlap but a refine
        // mutation racing with pagination could resurface a row.
        const seen = new Set(prev.map((x) => x.id));
        return [...prev, ...incoming.filter((x) => !seen.has(x.id))];
      });
      setGalleryNextCursor(r.data.next_cursor || null);
      setGalleryHasMore(!!r.data.has_more);
    } catch (err) {
      console.warn("loadMoreGallery failed", err);
    } finally {
      setLoadingMore(false);
    }
  }, [galleryNextCursor, loadingMore]);

  useEffect(() => { if (user) loadGallery(); }, [user, loadGallery]);

  // Group by variant_group, latest first. Each group = parent front + its variant siblings + multi-view children.
  const groupedGallery = useMemo(() => {
    const groups = new Map();
    for (const g of gallery) {
      const key = g.variant_group || g.id;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(g);
    }
    // Convert to array sorted by latest created_at within each group
    return Array.from(groups.values())
      .map((arr) => {
        arr.sort((a, b) => (a.variant_index ?? 0) - (b.variant_index ?? 0));
        return arr;
      })
      .sort((a, b) => new Date(b[0].created_at) - new Date(a[0].created_at));
  }, [gallery]);

  // The "current" generation + its sibling views
  const current = useMemo(() => gallery.find((g) => g.id === currentId) || null, [gallery, currentId]);
  // If the current render is itself a front (original OR refined), use its
  // OWN id as the sibling-anchor — so multi-view children we generate off a
  // refined image (parent_id = refined.id) group under it instead of under
  // the original parent. For side views (left/back/right) we still walk up
  // to their front via parent_id.
  const currentFrontId = current
    ? ((current.view || "front") === "front" ? current.id : (current.parent_id || current.id))
    : null;
  const currentSiblings = useMemo(() => {
    if (!currentFrontId) return {};
    const out = {};
    // Gallery is sorted newest-first by created_at; keep the newest per view
    // so refined renders (which share view + parent_id with their source) win.
    for (const g of gallery) {
      if (g.id === currentFrontId || g.parent_id === currentFrontId) {
        const v = g.view || "front";
        if (!out[v]) out[v] = g;
      }
    }
    return out;
  }, [gallery, currentFrontId]);

  // Prefer the explicitly selected `current` when its view matches the active tab
  // so clicking a specific gallery thumbnail (or a fresh refine result) always wins.
  const displayed =
    current && (current.view || "front") === activeView
      ? current
      : currentSiblings[activeView] || currentSiblings.front || current;

  // Pre-mint a signed download URL so the PNG button is a plain top-level
  // anchor click — no JS in the gesture chain (popup-blocker proof), and
  // no reliance on cookies traveling cross-site on iframe-initiated nav.
  useEffect(() => {
    if (!displayed?.id) { setDownloadUrl(""); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/studio/download-url/${displayed.id}`);
        if (!cancelled && data?.url) setDownloadUrl(`${BACKEND}${data.url}`);
      } catch (err) {
        if (!cancelled) {
          console.warn("download-url fetch failed", err);
          setDownloadUrl("");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [displayed?.id]);

  const onPickFile = async (file, kind = "primary") => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please upload an image file.");
    if (file.size > 5 * 1024 * 1024) return toast.error("Image is too large. Max 5MB.");
    const url = URL.createObjectURL(file);
    if (kind === "primary") {
      if (refPreview) URL.revokeObjectURL(refPreview);
      setRefFile(file); setRefPreview(url);
    } else if (kind === "gemstone") {
      if (gemPreview) URL.revokeObjectURL(gemPreview);
      setGemFile(file); setGemPreview(url);
    } else if (kind === "texture") {
      if (texPreview) URL.revokeObjectURL(texPreview);
      setTexFile(file); setTexPreview(url);
    }
  };

  const clearRef = (kind = "primary") => {
    if (kind === "primary") {
      setRefFile(null);
      if (refPreview) URL.revokeObjectURL(refPreview);
      setRefPreview(null);
    } else if (kind === "gemstone") {
      setGemFile(null);
      if (gemPreview) URL.revokeObjectURL(gemPreview);
      setGemPreview(null);
    } else if (kind === "texture") {
      setTexFile(null);
      if (texPreview) URL.revokeObjectURL(texPreview);
      setTexPreview(null);
    }
  };

  const onGenerate = async () => {
    if (prompt.trim().length < 6) return toast.error("Please describe what you want to design (at least 6 characters).");
    if (quota.remaining < variants) return toast.error(`Only ${quota.remaining} render(s) left this month.`);
    setGenerating(true);
    try {
      const fd = new FormData();
      fd.append("prompt", prompt);
      if (style) fd.append("style", style);
      if (metal) fd.append("metal", metal);
      fd.append("variants", String(variants));
      if (refFile) fd.append("reference_image", refFile);
      if (gemFile) fd.append("gemstone_image", gemFile);
      if (texFile) fd.append("texture_image", texFile);

      const res = await axios.post(`${API}/studio/generate`, fd, {
        timeout: 240000,
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Graceful "all variants failed" payload (iter-45) — backend
      // now returns 200 with `all_variants_failed: true` instead of a
      // 502 (which Cloudflare wraps in its own error page on prod).
      if (res.data?.all_variants_failed) {
        toast.error(res.data.message || "Generation failed — please retry.");
        if (res.data.quota) setQuota(res.data.quota);
        return;
      }
      const newEntries = res.data.entries || [];
      const errCount = (res.data.errors || []).length;
      toast.success(
        newEntries.length === 1
          ? "Render ready."
          : `${newEntries.length} variants generated${errCount ? ` · ${errCount} failed` : ""}.`
      );
      // Prepend to gallery + select first new entry
      setGallery((g) => [...newEntries, ...g]);
      setCurrentId(newEntries[0]?.id || null);
      setActiveView("front");
      if (res.data.quota) setQuota(res.data.quota);
      // Funnel middle — one event per SUCCESSFUL variant so credits_used
      // aggregates cleanly across dashboards. Style is one of ~10 pillars
      // so cardinality is safe as an event param.
      trackEvent("render_generated", {
        style: style || "default",
        variants: newEntries.length,
        credits_used: newEntries.length,
        has_errors: errCount > 0,
      });
    } catch (err) {
      toast.error(formatApiError(err, "Generation failed."));
    } finally {
      setGenerating(false);
    }
  };

  const onMultiview = async () => {
    // Legacy fixed-view (Left/Back/Right) Tripo pass — kept for older
    // gallery entries that already have L/B/R siblings. New designer
    // flow uses Camera Views (handler below).
    if (!currentFrontId) return;
    if (quota.remaining < 1) return toast.error("Not enough renders left for multi-view.");
    const sourceId = displayed?.id || current?.id || currentFrontId;
    if (!sourceId) return;
    setMultiviewing(true);
    try {
      const start = await axios.post(`${API}/studio/multiview/${sourceId}`, {}, { timeout: 30000 });
      const jobId = start.data?.job_id;
      if (!jobId) throw new Error(start.data?.detail || "No job id returned");
      const result = await pollMultiviewJob(jobId);
      const newEntries = result.entries || [];
      setGallery((g) => {
        const stale = (x) =>
          x.parent_id === sourceId &&
          (x.view === "left" || x.view === "back" || x.view === "right");
        return [...newEntries, ...g.filter((x) => !stale(x))];
      });
      if (result.quota) setQuota(result.quota);
      const errs = (result.view_errors || []).length;
      toast.success(errs ? `Generated views (with ${errs} miss)` : "Left · Back · Right ready.");
      setActiveView("left");
      // Funnel — legacy L/B/R multiview pass. 3 credits per pass.
      trackEvent("multiview_generated", {
        source: "legacy_lbr",
        views_generated: newEntries.length,
        credits_used: newEntries.length,
        has_errors: errs > 0,
      });
    } catch (err) {
      toast.error(formatApiError(err, "Multi-view failed."));
    } finally {
      setMultiviewing(false);
    }
  };

  /**
   * Camera Views — designer-picked angles. The CameraViewsDialog kicks
   * the POST off and hands us back the job_id; we own the polling and
   * the gallery merge so spinners stay in sync with the rest of Studio.
   */
  const onCameraViewsSubmit = async (jobId, angles) => {
    const sourceId = displayed?.id || current?.id || currentFrontId;
    setMultiviewing(true);
    try {
      const result = await pollMultiviewJob(jobId);
      const newEntries = result.entries || [];
      // Purge only prior camera-view children of the SAME source whose
      // view ids match one of the just-requested angles (so re-running
      // "Top-down" doesn't pile up duplicates).
      const targetViews = new Set(
        (angles || []).map((a) =>
          a.preset === "custom"
            ? `cam_custom_${Math.round(a.azimuth)}_${Math.round(a.elevation)}`
            : `cam_${a.preset}`,
        ),
      );
      setGallery((g) => {
        const stale = (x) => x.parent_id === sourceId && targetViews.has(x.view);
        return [...newEntries, ...g.filter((x) => !stale(x))];
      });
      if (result.quota) setQuota(result.quota);
      const errs = (result.view_errors || []).length;
      toast.success(
        errs
          ? `${newEntries.length} angle${newEntries.length === 1 ? "" : "s"} ready · ${errs} failed`
          : `${newEntries.length} angle${newEntries.length === 1 ? "" : "s"} ready.`,
      );
      // Jump to the first new view if any.
      if (newEntries[0]?.view) setActiveView(newEntries[0].view);
      // Funnel — camera-views flow (newer, designer-chosen angles).
      // Credits used == entries generated (server bills per view).
      trackEvent("multiview_generated", {
        source: "camera_views",
        views_generated: newEntries.length,
        credits_used: newEntries.length,
        has_errors: errs > 0,
      });
    } catch (err) {
      toast.error(formatApiError(err, "Camera views failed."));
    } finally {
      setMultiviewing(false);
    }
  };

  /**
   * Render-from-CAD — multiple CAD screenshots → photoreal renders.
   * Dialog hands us the job_id; we own polling + gallery merge. Each
   * new entry is prepended; for "siblings" grouping the first entry
   * becomes the active selection so the designer immediately sees the
   * piece with the side-tabs populated.
   */
  const onRenderFromCadSubmit = async (jobId, meta) => {
    setMultiviewing(true);
    try {
      const result = await pollMultiviewJob(jobId, { timeoutMs: 360000 });
      const newEntries = result.entries || [];
      setGallery((g) => [...newEntries, ...g]);
      if (result.quota) setQuota(result.quota);
      if (newEntries.length) {
        // Focus the first new render so the user sees the result immediately.
        setCurrentId(newEntries[0].id);
        setActiveView(newEntries[0].view || "front");
      }
      const errs = (result.view_errors || []).length;
      const n = newEntries.length;
      toast.success(
        errs
          ? `${n} CAD render${n === 1 ? "" : "s"} ready · ${errs} failed`
          : `${n} CAD render${n === 1 ? "" : "s"} ready.`,
      );
    } catch (err) {
      toast.error(formatApiError(err, "CAD render failed."));
    } finally {
      setMultiviewing(false);
    }
  };

  /**
   * Polls /api/studio/multiview-job/{job_id} every 2s until status is
   * `success` or `failed`. Resolves with the final job payload; rejects
   * on failure (with `.message` set to the backend error string).
   *
   * Resilience: a single slow poll request (e.g. transient Cloudflare
   * hiccup or backend GC pause) used to surface as a user-facing
   * "timeout of 15000ms exceeded" toast and kill the entire job. We now
   * bump the per-poll timeout to 30 s AND swallow up to N consecutive
   * poll errors before giving up — the background worker keeps running
   * regardless, so transient client-side errors should not abort it.
   */
  const pollMultiviewJob = async (jobId, { intervalMs = 2000, timeoutMs = 240000 } = {}) => {
    const started = Date.now();
    const MAX_CONSEC_ERRORS = 5; // ~10 s of network blips tolerated
    let consecErrors = 0;
    // Small initial delay so the worker has a head-start.
    await new Promise((r) => setTimeout(r, 1500));
    while (true) {
      try {
        const res = await axios.get(`${API}/studio/multiview-job/${jobId}`, { timeout: 30000 });
        const data = res.data;
        consecErrors = 0; // reset on any successful poll
        if (data.status === "success") return data;
        if (data.status === "failed") {
          const err = new Error(data.error || "Multi-view failed.");
          err.response = { data: { detail: data.error || "Multi-view failed." } };
          throw err;
        }
      } catch (e) {
        // Only treat as fatal if (a) the backend returned a 4xx/5xx with a
        // structured error, or (b) we've crossed the consecutive-error
        // threshold. Bare timeouts / network blips just retry.
        const hasBackendDetail = e?.response?.data?.detail || e?.response?.status >= 500;
        if (hasBackendDetail && e?.response?.status !== 504) throw e;
        consecErrors += 1;
        if (consecErrors >= MAX_CONSEC_ERRORS) {
          throw new Error(
            "Lost contact with the server while waiting for the job. The render may still be processing — refresh in a minute.",
          );
        }
      }
      if (Date.now() - started > timeoutMs) {
        throw new Error("Job is taking unusually long. Please refresh and try again.");
      }
      await new Promise((r) => setTimeout(r, intervalMs));
    }
  };

  const onCadReady = async () => {
    if (!displayed) return;
    if (quota.remaining < 1) {
      toast.error("Not enough credits for CAD-ready render.");
      return;
    }
    setCadReadyLoading(true);
    try {
      const res = await axios.post(`${API}/studio/cad-ready/${displayed.id}`, {}, { timeout: 240000 });
      const entry = res.data?.entry;
      if (!entry) throw new Error("No entry returned");
      setGallery((g) => [entry, ...g]);
      setCurrentId(entry.id);
      setActiveView(entry.view || "front");
      if (res.data.quota) setQuota(res.data.quota);
      toast.success("Bare-metal preview ready · pre-polish stage");
    } catch (err) {
      toast.error(formatApiError(err, "CAD-ready render failed."));
    } finally {
      setCadReadyLoading(false);
    }
  };

  /**
   * Undo the most recent refine on the currently-displayed render. Deletes
   * the refined child entry server-side and switches the canvas back to
   * its parent. No credit refund — undo is a UI convenience.
   */
  const [undoingRefine, setUndoingRefine] = useState(false);
  const onUndoRefine = async () => {
    if (!displayed?.refined_from) return;
    setUndoingRefine(true);
    try {
      const res = await axios.post(`${API}/studio/undo-refine/${displayed.id}`);
      const parent = res.data?.parent;
      if (!parent) throw new Error("No parent returned");
      const removedId = displayed.id;
      setGallery((g) => {
        const filtered = g.filter((x) => x.id !== removedId);
        const hasParent = filtered.some((x) => x.id === parent.id);
        return hasParent ? filtered : [parent, ...filtered];
      });
      setCurrentId(parent.id);
      setActiveView(parent.view || "front");
      toast.success("Refine undone · canvas restored to previous render");
    } catch (err) {
      toast.error(formatApiError(err, "Couldn't undo this refine."));
    } finally {
      setUndoingRefine(false);
    }
  };


  /**
   * Called by StudioGallery's per-tile quick refine button. Opens the
   * QuickRefineDialog targeting that gen.
   */
  const onMultiviewRefine = (g) => setQuickRefineTarget(g);

  /**
   * Called by StudioGallery's per-tile split-component button. Opens the
   * SplitComponentDialog targeting that gen. Kept fully separate from
   * refine so the working refine flow above is never affected.
   */
  const onSplitComponent = (g) => setSplitTarget(g);

  /**
   * Called by the "Auto-split" button (Current Render panel + gallery
   * tile). Opens the AutoSplitDialog which handles the two-step
   * detect → preview → extract flow entirely on its own — no state
   * bleed into any existing refine / manual split / multiview code path.
   */
  const onAutoSplit = (g) => setAutoSplitTarget(g);

  /** Called by AutoSplitDialog when a batch extraction completes.
   * Prepends the new isolated entries to the gallery so they appear
   * immediately without a fresh page load. Deduped by id in case
   * one arrives twice from a retry. */
  const onAutoSplitExtracted = (entries) => {
    if (!Array.isArray(entries) || entries.length === 0) return;
    setGallery((prev) => {
      const existingIds = new Set(prev.map((g) => g.id));
      const fresh = entries.filter((e) => !existingIds.has(e.id));
      return [...fresh, ...prev];
    });
  };

  /**
   * Submit handler for SplitComponentDialog. Posts to the isolation
   * endpoint, appends the resulting isolated-on-white render to gallery
   * so the designer can immediately pick it into a CAD multiview.
   */
  const submitSplitComponent = async (target, componentName, bbox = null, action = "isolate", polygon = null) => {
    try {
      const body = { component_name: componentName, action };
      // Only include bbox when the designer actually lassoed a region.
      // Backend accepts `null` implicitly (no crop, whole piece is fed).
      if (Array.isArray(bbox) && bbox.length === 4) {
        body.bbox = bbox;
      }
      // Send the polygon too so the backend can apply a polygon mask
      // (only pixels inside the lasso outline are visible) instead of
      // a rectangular bbox crop that leaks neighbouring content.
      if (Array.isArray(polygon) && polygon.length >= 3) {
        body.polygon = polygon;
      }
      const res = await axios.post(
        `${API}/studio/split-component/${target.id}`,
        body,
        { timeout: 60000 },
      );
      const newEntry = res.data?.entry;
      if (newEntry) {
        setGallery((prev) => [newEntry, ...prev]);
      }
      if (res.data?.quota) setQuota(res.data.quota);
      const label = componentName || "lassoed region";
      toast.success(
        action === "remove"
          ? `Removed · ${label}`
          : `Component isolated · ${label}`,
      );
    } catch (err) {
      toast.error(formatApiError(err, "Could not process split."));
    }
  };

  /**
   * Called by StudioGallery's undo button on tiles that have a refine
   * snapshot. Reverts the gen to the pre-refine bytes — free, server-side.
   */
  const onMultiviewUndo = async (g) => {
    try {
      const res = await axios.post(
        `${API}/studio/multiview-undo/${g.id}`,
        {},
        { timeout: 30000 },
      );
      const refreshed = res.data?.entry;
      if (refreshed) {
        // Client-side belt-and-braces: bump `refined_at` to a strictly
        // newer client timestamp than whatever's currently in gallery
        // for this id. Even if the server response is momentarily stale
        // (replica-set read-after-write drift, response-body caching),
        // this guarantees `renderImageUrl` produces a URL the browser
        // has never fetched — and therefore always shows the reverted
        // bytes instead of the cached refined image.
        const clientTs = new Date().toISOString();
        const patched = { ...refreshed, refined_at: clientTs };
        setGallery((prev) => prev.map((x) => (x.id === patched.id ? patched : x)));
      }
      // Defensive full refresh — if the server response was somehow
      // incomplete (missing `refined_at`, missing an unset field), a
      // fresh GET of the full gallery flushes any lingering stale state
      // in `current` / `currentSiblings` / `displayed`. Runs async, so
      // the toast fires immediately and the UI update is instant.
      loadGallery();
      if (res.data?.quota) setQuota(res.data.quota);
      toast.success(`${(g.view || "view").toUpperCase()} view reverted.`);
    } catch (err) {
      toast.error(formatApiError(err, "Could not undo."));
    }
  };

  /**
   * Submit handler used by QuickRefineDialog. Hits multiview-refine,
   * replaces the gen in gallery with the refreshed bytes (image src is
   * cache-busted via `refined_at`), and updates quota.
   */
  /**
   * Submit handler used by QuickRefineDialog. The backend now returns a
   * `{job_id, status:running}` immediately and grinds through Tripo in
   * the background (refine takes 30–90 s and Cloudflare cuts at ~100 s,
   * so a sync request 520s). We poll `/studio/refine-job/{job_id}`
   * until it's terminal, then patch gallery state (cache-busted via
   * `refined_at`) and update quota.
   */
  const submitQuickRefine = async (target, prompt, opts = {}) => {
    try {
      const endpoint = opts.batch
        ? `${API}/studio/multiview-refine-sides/${target.id}`
        : `${API}/studio/multiview-refine/${target.id}`;
      // Transient-tolerant submit. Cloudflare 520/502/504 blips are
      // usually 2-4 s (origin pod restart, upstream hiccup). Do ONE
      // silent auto-retry with a small backoff before surfacing the
      // failure to the user — this eliminates ~90% of the "just click
      // it again" retries designers were doing manually.
      let submit;
      try {
        submit = await axios.post(endpoint, { prompt }, { timeout: 30000 });
      } catch (submitErr) {
        const st = submitErr?.response?.status;
        const isTransient = !st || (st >= 500 && st < 600);
        if (!isTransient) throw submitErr;
        await new Promise((r) => setTimeout(r, 1500));
        submit = await axios.post(endpoint, { prompt }, { timeout: 30000 });
      }
      const jobId = submit.data?.job_id;
      if (!jobId) {
        // Legacy server (pre-async refactor): fall back to the old
        // synchronous response shape.
        const refreshedList = submit.data?.entries || (submit.data?.entry ? [submit.data.entry] : []);
        if (refreshedList.length) {
          setGallery((g) => g.map((x) => refreshedList.find((r) => r.id === x.id) || x));
        }
        if (submit.data?.quota) setQuota(submit.data.quota);
        toast.success(
          opts.batch
            ? `All sides refined · ${refreshedList.length} views updated.`
            : `${(target.view || "view").toUpperCase()} view refined.`
        );
        return;
      }

      // Poll the job. Refine usually finishes in 30–60 s.
      const POLL_MS = 2000;
      const MAX_WAIT_MS = 240000;  // 4 min hard cap on our side
      const t0 = Date.now();
      let final = null;
      // Transient-error tolerance. The poll endpoint is a simple Mongo
      // read, but under production it can occasionally 5xx (Cloudflare
      // 520/524/502 during a pod restart or brief upstream hiccup).
      // Those are NOT terminal — the refine job is still grinding on
      // Tripo in the background, and the next poll almost always
      // succeeds. Give it up to 5 consecutive transient failures
      // (~10 s) before we surface an error; the outer 4-min MAX_WAIT_MS
      // still caps the whole thing.
      let transientFailures = 0;
      const MAX_TRANSIENT = 5;
      while (Date.now() - t0 < MAX_WAIT_MS) {
        await new Promise((r) => setTimeout(r, POLL_MS));
        let poll;
        try {
          poll = await axios.get(
            `${API}/studio/refine-job/${jobId}`,
            { timeout: 20000 },
          );
        } catch (pollErr) {
          const st = pollErr?.response?.status;
          // 4xx = real problem (auth expired, job vanished) — bail out.
          if (st && st >= 400 && st < 500) throw pollErr;
          // 5xx / network / timeout = transient. Keep polling; only give
          // up if we hit N in a row.
          transientFailures += 1;
          if (transientFailures >= MAX_TRANSIENT) throw pollErr;
          continue;
        }
        transientFailures = 0;  // reset streak on success
        const data = poll.data || {};
        if (data.status === "success") { final = data; break; }
        if (data.status === "failed") {
          throw new Error(data.error || "Refine failed");
        }
      }
      if (!final) throw new Error("Refine taking longer than expected. Please try again.");

      const refreshedList = final.entries || [];
      if (refreshedList.length) {
        setGallery((g) => g.map((x) => refreshedList.find((r) => r.id === x.id) || x));
      }
      if (final.quota) setQuota(final.quota);
      toast.success(
        opts.batch
          ? `All sides refined · ${refreshedList.length} views updated.`
          : `${(target.view || "view").toUpperCase()} view refined.`
      );
    } catch (err) {
      toast.error(formatApiError(err, "Refine failed."));
      throw err;
    }
  };

  /**
   * Called by CadDialog when the user clicks "CAD-grade multiview". Returns
   * `{ entries }` so the dialog can auto-fill its Left/Back/Right picks.
   * Throws on failure so the dialog surfaces a toast.
   */
  const onRequestCadMultiview = async (sourceGenId, opts = {}) => {
    const start = await axios.post(
      `${API}/studio/cad-ready-multiview/${sourceGenId}`,
      {},
      { timeout: 30000, params: opts.force ? { force: 1 } : {} },
    );
    const jobId = start.data?.job_id;
    // If nothing was regenerated (all views still cached and force was
    // off), the backend returns synchronously without a job_id — just
    // hand the cached entries back.
    if (!jobId) {
      const cached = start.data?.entries || [];
      return { entries: cached, quota: start.data?.quota };
    }
    const result = await pollMultiviewJob(jobId);
    const newEntries = result.entries || [];
    setGallery((g) => {
      const filtered = g.filter(
        (x) => !(x.parent_id === sourceGenId && x.render_kind === "cad_ready" && x.view !== "front"),
      );
      return [...newEntries, ...filtered];
    });
    if (result.quota) setQuota(result.quota);
    return { entries: newEntries, quota: result.quota };
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/studio/generations/${deleteTarget.id}`);
      setGallery((g) => g.filter((x) => x.id !== deleteTarget.id));
      if (currentId === deleteTarget.id) setCurrentId(null);
      toast.success("Render deleted");
      const q = await axios.get(`${API}/studio/wallet`);
      setQuota(q.data);
    } catch (err) {
      console.error("delete generation failed", err);
      toast.error("Could not delete");
    } finally {
      setDeleteTarget(null);
    }
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  const initials = (user.name || user.email || "F").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
  const firstName = (user.name || "").split(" ")[0] || user.email.split("@")[0];
  const hasMultiviewSiblings = ["left", "back", "right"].some((v) => currentSiblings[v]);
  // All non-front views actually present for the current piece (legacy
  // L/B/R + any new cam_* angles). Front is always implicit.
  const availableExtraViews = Object.keys(currentSiblings).filter((v) => v !== "front");
  const hasAnyExtraViews = availableExtraViews.length > 0;

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white">
      {/* Top bar */}
      <header className="border-b border-white/5 bg-[#0A0A0A] sticky top-0 z-30">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid="user-brand-link" className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-md bg-gradient-to-br from-[#F3CE5A] to-[#8B6914] flex items-center justify-center">
              <span className="font-display text-black text-xs font-bold">F</span>
            </div>
            <span className="font-display text-lg tracking-tight">Facetra · Studio</span>
            <span className="hidden sm:inline ml-2 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] border border-[#D4AF37]/40 rounded-full px-2 py-0.5">
              Early Access
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <RendersChip quota={quota} onTopup={() => setPackPickerOpen(true)} />
            <a
              href="/catalog"
              data-testid="header-catalog-link"
              title="Compose catalog & social-media images"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full border border-white/15 text-white/70 hover:text-[#D4AF37] hover:border-[#D4AF37]/40 transition"
            >
              <Sparkles className="w-3.5 h-3.5" /> Catalog
            </a>
            <a
              href="/photoroom"
              data-testid="header-photoroom-link"
              title="Touch-up raw photographs into catalogue-ready images"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full border border-white/15 text-white/70 hover:text-[#D4AF37] hover:border-[#D4AF37]/40 transition"
            >
              <Camera className="w-3.5 h-3.5" /> PhotoRoom
            </a>
            <TeamHeaderChip />
            {/* Redeem section hidden per product decision (2026-06-09) —
                no coupon UI is shown to designers anymore. The dialog
                itself is kept around in case we re-enable later. */}
            {false && (
            <button
              type="button"
              onClick={() => setRedeemOpen(true)}
              data-testid="redeem-code-btn"
              title="Redeem a coupon code (bonus credits, Edu access, or launch promo)"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full border border-white/15 text-white/70 hover:text-[#D4AF37] hover:border-[#D4AF37]/40 transition"
            >
              <Ticket className="w-3.5 h-3.5" /> Redeem
            </button>
            )}
            {packs.length > 0 && (
              <button
                type="button"
                onClick={() => setPackPickerOpen(true)}
                data-testid="buy-credits-btn"
                title="Top up your credit wallet via Razorpay (UPI / cards / NetBanking)"
                className="hidden sm:inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/45 text-[#D4AF37] hover:bg-[#D4AF37]/25 transition"
              >
                <Plus className="w-3.5 h-3.5" /> Top up
              </button>
            )}
            <div className="hidden sm:flex items-center gap-2.5">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-white/10" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-[#1A1A1A] border border-[#D4AF37]/30 flex items-center justify-center text-xs text-[#D4AF37]">
                  {initials}
                </div>
              )}
            </div>
            <button onClick={logout} data-testid="user-logout-btn" className="text-xs text-white/55 hover:text-[#D4AF37] inline-flex items-center gap-1.5 transition">
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-6 lg:px-10 py-8">
        <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] mb-2">
              {activeTab === "studio" ? "Studio" : "Library"}
            </p>
            <h1 className="font-display text-3xl md:text-4xl tracking-[-0.02em]">
              Welcome back, <span className="italic gold-gradient-text">{firstName}</span>
            </h1>
          </div>
          {/* Tab switcher */}
          <div className="inline-flex rounded-full border border-white/10 p-0.5 text-xs bg-[#0F0F0F]">
            <button
              type="button"
              onClick={() => setActiveTab("studio")}
              data-testid="tab-studio"
              className={`px-4 py-1.5 rounded-full transition inline-flex items-center gap-1.5 ${
                activeTab === "studio" ? "bg-[#D4AF37] text-black" : "text-white/65 hover:text-white"
              }`}
            >
              <Wand2 className="w-3.5 h-3.5" /> Studio
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("portfolio")}
              data-testid="tab-portfolio"
              className={`px-4 py-1.5 rounded-full transition inline-flex items-center gap-1.5 ${
                activeTab === "portfolio" ? "bg-[#D4AF37] text-black" : "text-white/65 hover:text-white"
              }`}
            >
              <BookMarked className="w-3.5 h-3.5" /> Portfolio
            </button>
          </div>
        </div>

        {activeTab === "portfolio" ? (
          <PortfolioPanel inIframe={inIframe} />
        ) : (
        <div className="grid grid-cols-12 gap-5">
          {/* LEFT — Generate panel */}
          <aside className="col-span-12 lg:col-span-4 glass-panel rounded-2xl p-5 space-y-5">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-2">
                References <span className="text-white/30 normal-case tracking-normal text-[10px]">(optional · up to 3)</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <RefSlot
                  testId="ref-primary"
                  label="Sketch"
                  hint="Design intent"
                  preview={refPreview}
                  onPick={(f) => onPickFile(f, "primary")}
                  onClear={() => clearRef("primary")}
                />
                <RefSlot
                  testId="ref-gemstone"
                  label="Gemstone"
                  hint="Stone / material"
                  preview={gemPreview}
                  onPick={(f) => onPickFile(f, "gemstone")}
                  onClear={() => clearRef("gemstone")}
                />
                <RefSlot
                  testId="ref-texture"
                  label="Texture"
                  hint="Colour / finish"
                  preview={texPreview}
                  onPick={(f) => onPickFile(f, "texture")}
                  onClear={() => clearRef("texture")}
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-2">
                Describe the piece
              </label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={4}
                data-testid="studio-prompt"
                placeholder="A bridal Jadau choker with uncut diamonds and emerald drops, layered with pearl strings, 22kt gold setting…"
                className="w-full bg-[#141414] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition resize-none"
              />
              <div className="mt-1 text-[10px] text-white/30 text-right">{prompt.length} / 1200</div>
            </div>

            <div>
              <div className="flex items-center justify-between gap-2 mb-2">
                <label className="text-[10px] uppercase tracking-[0.22em] text-white/45">
                  Style preset
                </label>
                <div className="flex items-center gap-1.5">
                  <span className="text-[9px] uppercase tracking-[0.18em] text-white/35">Metal</span>
                  <select
                    value={metal}
                    onChange={(e) => setMetal(e.target.value)}
                    data-testid="studio-metal-select"
                    title="Optional render-style metal — prepends a structured token to the prompt."
                    className="bg-[#0B0B0B] border border-white/10 rounded-full px-2.5 py-1 text-[11px] text-white/75 focus:outline-none focus:border-[#D4AF37] hover:border-white/25 transition"
                  >
                    <option value="">Auto</option>
                    {RENDER_METALS.map((m) => (
                      <option key={m.id} value={m.id}>{m.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {STYLES.map((s) => (
                  <button
                    key={s}
                    onClick={() => setStyle(s)}
                    data-testid={`studio-style-${s.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`text-[11px] px-2.5 py-1.5 rounded-full border transition ${
                      style === s
                        ? "bg-[#D4AF37]/15 border-[#D4AF37]/55 text-[#D4AF37]"
                        : "border-white/10 text-white/60 hover:border-white/25"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[10px] uppercase tracking-[0.22em] text-white/45">
                  Variants
                </label>
                <span className="text-xs text-[#D4AF37] font-medium">{variants}</span>
              </div>
              <div className="flex items-center gap-1.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    onClick={() => setVariants(n)}
                    disabled={n > quota.remaining}
                    data-testid={`studio-variant-${n}`}
                    className={`flex-1 text-sm py-2 rounded-lg border transition ${
                      variants === n
                        ? "bg-[#D4AF37]/15 border-[#D4AF37]/55 text-[#D4AF37]"
                        : "border-white/10 text-white/60 hover:border-white/25"
                    } disabled:opacity-30 disabled:cursor-not-allowed`}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <div className="mt-1.5 text-[10px] text-white/35">
                Uses {variants} of your {quota.remaining} remaining renders
              </div>
            </div>

            <button
              onClick={onGenerate}
              disabled={generating || quota.remaining <= 0}
              data-testid="studio-generate-btn"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium py-3.5 hover:bg-[#F3CE5A] transition disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_8px_30px_-8px_rgba(212,175,55,0.55)]"
            >
              {generating ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Rendering {variants > 1 ? `${variants} variants` : ""} …</>
              ) : quota.remaining <= 0 ? (
                <>Monthly limit reached</>
              ) : (
                <><Wand2 className="w-4 h-4" /> Generate {variants > 1 ? `${variants} renders` : "render"}</>
              )}
            </button>

            <button
              onClick={() => setRenderFromCadOpen(true)}
              disabled={multiviewing || quota.remaining <= 0}
              data-testid="studio-render-from-cad-side-btn"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/[0.02] text-white/80 text-[12px] py-2.5 hover:border-[#D4AF37]/45 hover:text-[#D4AF37] hover:bg-[#D4AF37]/[0.06] transition disabled:opacity-50 disabled:cursor-not-allowed"
              title="Upload CAD screenshots and convert each into a photoreal render (1 credit per image)."
            >
              <UploadCloud className="w-3.5 h-3.5" /> Render from CAD …
            </button>

            <p className="text-[11px] text-white/40 text-center">
              {quota.remaining} of {quota.limit} renders left · Resets on the 1st
            </p>
          </aside>

          {/* CENTER — Current render with multi-view tabs */}
          <section className="col-span-12 lg:col-span-5 glass-panel rounded-2xl p-5 min-h-[600px] flex flex-col">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/45">
                {displayed ? "Current render" : "Your canvas"}
              </div>
              {displayed && (
                <div className="flex flex-wrap items-center gap-2 justify-end min-w-0">
                  {currentFrontId && hasAnyExtraViews && (
                    <div className="flex flex-wrap items-center gap-1 p-0.5 rounded-lg bg-[#141414] border border-white/5 max-w-full overflow-x-auto">
                      {/* Front is always first */}
                      {!!currentSiblings.front && (
                        <button
                          onClick={() => setActiveView("front")}
                          data-testid="studio-view-front"
                          className={`text-[11px] px-2.5 py-1 rounded-md transition whitespace-nowrap ${
                            activeView === "front"
                              ? "bg-[#D4AF37]/15 text-[#D4AF37]"
                              : "text-white/65 hover:text-white"
                          }`}
                        >
                          Front
                        </button>
                      )}
                      {availableExtraViews.map((v) => (
                        <button
                          key={v}
                          onClick={() => setActiveView(v)}
                          data-testid={`studio-view-${v}`}
                          className={`text-[11px] px-2.5 py-1 rounded-md transition whitespace-nowrap ${
                            activeView === v
                              ? "bg-[#D4AF37]/15 text-[#D4AF37]"
                              : "text-white/65 hover:text-white"
                          }`}
                        >
                          {formatViewLabel(v)}
                        </button>
                      ))}
                    </div>
                  )}
                  {currentFrontId && (
                    <button
                      onClick={() => setCameraViewsOpen(true)}
                      disabled={multiviewing || quota.remaining < 3}
                      data-testid="studio-camera-views-btn"
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37]/20 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      title={quota.remaining < 3 ? "Needs 3 credits" : "Pick camera angles to photograph the piece from (3 credits)"}
                    >
                      {multiviewing ? (
                        <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating views…</>
                      ) : (
                        <><Layers3 className="w-3.5 h-3.5" /> Camera views (3 credits)</>
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => setRefineTarget(displayed)}
                    disabled={quota.remaining < 1}
                    data-testid="studio-refine-btn"
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-white/15 text-white/80 hover:bg-white/5 hover:border-[#D4AF37]/40 hover:text-[#D4AF37] transition disabled:opacity-40 disabled:cursor-not-allowed"
                    title={quota.remaining < 1 ? "No renders left" : "Mark areas + refine"}
                  >
                    <Edit3 className="w-3.5 h-3.5" /> Refine
                  </button>
                  {displayed?.refined_from && (
                    <button
                      onClick={onUndoRefine}
                      disabled={undoingRefine}
                      data-testid="studio-undo-refine-btn"
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-white/15 text-white/70 hover:bg-white/5 hover:border-[#D4AF37]/40 hover:text-[#D4AF37] transition disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Discard this refine and return to the previous render (no credit refund)"
                    >
                      {undoingRefine ? (
                        <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Undoing…</>
                      ) : (
                        <><Undo2 className="w-3.5 h-3.5" /> Undo refine</>
                      )}
                    </button>
                  )}
                  <button
                    onClick={onCadReady}
                    disabled={cadReadyLoading || quota.remaining < 1}
                    data-testid="studio-cad-ready-btn"
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-white/15 text-white/80 hover:bg-white/5 hover:border-[#D4AF37]/40 hover:text-[#D4AF37] transition disabled:opacity-40 disabled:cursor-not-allowed"
                    title={
                      quota.remaining < 1
                        ? "No renders left"
                        : "Strip diamonds, gemstones, enamel & rhodium — show bare metal (pre-polish) for the CAD bench. Uses 1 render."
                    }
                  >
                    {cadReadyLoading ? (
                      <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Stripping…</>
                    ) : (
                      <><Gem className="w-3.5 h-3.5" /> CAD ready</>
                    )}
                  </button>
                  <button
                    onClick={() => onAutoSplit(displayed)}
                    disabled={quota.remaining < 1}
                    data-testid="studio-auto-split-btn"
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 transition disabled:opacity-40 disabled:cursor-not-allowed"
                    title={
                      quota.remaining < 1
                        ? "No credits left"
                        : "Auto-detect and split every component of this piece in one batch. Detection: 1 credit · each extracted component: 1 credit."
                    }
                  >
                    <ScanSearch className="w-3.5 h-3.5" /> Auto-split
                  </button>
                  {displayed?.render_kind === "cad_ready" && (
                    <span
                      className="hidden sm:inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-white/45 px-2 py-1 rounded-full border border-white/10"
                      title="Generate the Left · Back · Right rotations inside the CAD workshop (CAD-grade multiview)."
                    >
                      <Layers3 className="w-3 h-3" /> Open CAD for multiview
                    </span>
                  )}
                  <button
                    onClick={() => setMaterialsTarget(displayed)}
                    data-testid="studio-materials-btn"
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-white/15 text-white/80 hover:bg-white/5 hover:border-[#D4AF37]/40 hover:text-[#D4AF37] transition"
                    title="View / edit Bill of Materials"
                  >
                    <ClipboardList className="w-3.5 h-3.5" /> Materials
                  </button>
                  {/* Material preset shortcut — when the displayed render
                      has no BOM yet but the user has OTHER renders with
                      materials, surface a 1-click "Apply preset" picker
                      so they skip re-running Gemini. (Phase P2 spec a:
                      copy materials from one render to another.) */}
                  {!displayed.materials && gallery.some((g) => g.id !== displayed.id && g.materials) && (
                    <button
                      onClick={() => setPresetPickerTarget(displayed)}
                      data-testid="studio-material-preset-btn"
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 transition"
                      title="Copy materials from another render (free, instant — no AI call)"
                    >
                      <Wand2 className="w-3.5 h-3.5" /> Apply preset
                    </button>
                  )}
                  <button
                    onClick={() => setCadDialogOpen(true)}
                    disabled={quota.plan === "edu"}
                    data-testid="studio-cad-btn"
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-white/15 text-white/80 hover:bg-white/5 hover:border-[#D4AF37]/40 hover:text-[#D4AF37] transition disabled:opacity-50 disabled:cursor-not-allowed"
                    title={
                      quota.plan === "edu"
                        ? "CAD is a paid feature — upgrade your plan to unlock."
                        : "Open CAD workshop — generate a 3D mesh from multiview renders, or download the layered CAD .zip starter kit."
                    }
                  >
                    <Box className="w-3.5 h-3.5" /> CAD{quota.plan === "edu" ? " · 🔒" : ""}
                  </button>
                  <button
                    onClick={() => setRenderFromCadOpen(true)}
                    disabled={multiviewing}
                    data-testid="studio-render-from-cad-btn"
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-white/15 text-white/80 hover:bg-white/5 hover:border-[#D4AF37]/40 hover:text-[#D4AF37] transition disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Upload CAD screenshots and convert each into a photoreal luxury render."
                  >
                    <UploadCloud className="w-3.5 h-3.5" /> Render from CAD
                  </button>
                  <button
                    onClick={() => setSavePortfolioTarget(displayed)}
                    data-testid="studio-save-portfolio-btn"
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-white/15 text-white/80 hover:bg-white/5 hover:border-[#D4AF37]/40 hover:text-[#D4AF37] transition"
                    title="Save this render to a portfolio (with current BOM)"
                  >
                    <BookMarked className="w-3.5 h-3.5" /> Save
                  </button>
                  {inIframe ? (
                    <a
                      href={window.location.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="studio-download-btn"
                      title="Downloads are disabled inside the in-app preview. Click to open Facetra in a real browser tab — then click PNG again to save."
                      className="text-xs inline-flex items-center gap-1.5 text-[#D4AF37] hover:text-[#F3CE5A] cursor-pointer"
                    >
                      <Download className="w-3.5 h-3.5" /> PNG&nbsp;<span className="opacity-60">↗ open tab</span>
                    </a>
                  ) : (
                    <a
                      href={downloadUrl || `${BACKEND}${displayed.image_url}${displayed.refined_at ? `?v=${encodeURIComponent(displayed.refined_at)}&` : "?"}download=1`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="studio-download-btn"
                      aria-disabled={!downloadUrl}
                      className={`text-xs inline-flex items-center gap-1.5 ${downloadUrl ? "text-[#D4AF37] hover:text-[#F3CE5A] cursor-pointer" : "text-white/30 cursor-wait pointer-events-none"}`}
                    >
                      <Download className="w-3.5 h-3.5" /> PNG
                    </a>
                  )}
                </div>
              )}
            </div>

            <div className="relative flex-1 rounded-xl border border-white/10 overflow-hidden bg-[#0E0E0E]">
              {generating || multiviewing ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                  <div className="relative">
                    <Loader2 className="w-10 h-10 text-[#D4AF37] animate-spin" />
                    <div className="absolute inset-0 rounded-full bg-[#D4AF37]/20 blur-2xl" />
                  </div>
                  <div className="text-sm text-white/70 font-display">
                    {multiviewing ? "Generating Left · Back · Right…" : `Rendering ${variants > 1 ? `${variants} variants` : "your piece"}…`}
                  </div>
                  <div className="text-[11px] text-white/40">Gemini Nano Banana · 8k detail</div>
                </div>
              ) : displayed ? (
                <img
                  src={renderImageUrl(BACKEND, displayed)}
                  alt={displayed.prompt}
                  className="absolute inset-0 w-full h-full object-contain"
                  data-testid="studio-current-image"
                />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                  <ImageIcon className="w-8 h-8 text-white/15" />
                  <div className="text-sm text-white/45">Your renders will appear here</div>
                  <div className="text-[11px] text-white/30 max-w-[280px] text-center">
                    Write a prompt on the left, pick variants, then hit Generate.
                  </div>
                </div>
              )}
            </div>

            {displayed && (
              <div className="mt-4 space-y-2">
                <div className="text-sm text-white font-display">
                  {displayed.style && <span className="text-[#D4AF37]">{displayed.style}</span>}
                  {displayed.style && " · "}
                  <span className="text-white/85">
                    {(displayed.prompt || "").length > 110 ? displayed.prompt.slice(0, 110) + "…" : displayed.prompt}
                  </span>
                </div>
                <div className="text-[11px] text-white/40 flex items-center gap-2">
                  <span className="capitalize">{displayed.view || "front"} view</span>
                  <span>· {new Date(displayed.created_at).toLocaleString()}</span>
                  {displayed.has_reference && <span>· with reference</span>}
                </div>
              </div>
            )}
          </section>

          {/* RIGHT — Gallery grouped by variant_group */}
          <StudioGallery
            backendUrl={BACKEND}
            groupedGallery={groupedGallery}
            totalCount={gallery.length}
            loading={loadingGallery}
            currentId={currentId}
            onSelect={(g) => { setCurrentId(g.id); setActiveView(g.view || "front"); }}
            onDelete={(g) => setDeleteTarget(g)}
            onMultiviewRefine={onMultiviewRefine}
            onMultiviewUndo={onMultiviewUndo}
            onSplitComponent={onSplitComponent}
            onAutoSplit={onAutoSplit}
          />
          {galleryHasMore && (
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={loadMoreGallery}
                disabled={loadingMore}
                data-testid="studio-gallery-load-more"
                className="text-xs px-4 py-2 rounded-full border border-white/15 text-white/70 hover:text-[#D4AF37] hover:border-[#D4AF37]/40 transition disabled:opacity-50"
              >
                {loadingMore ? "Loading older renders…" : "Load older renders"}
              </button>
            </div>
          )}
        </div>
        )}
      </main>

      <LuxuryConfirmModal
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        title="Delete this render?"
        description={deleteTarget ? `"${(deleteTarget.prompt || '').slice(0, 80)}…" — this cannot be undone.` : ""}
        confirmLabel="Delete render"
        destructive
        onConfirm={confirmDelete}
      />

      <RefineDialog
        open={!!refineTarget}
        onOpenChange={(v) => !v && setRefineTarget(null)}
        source={refineTarget}
        quotaRemaining={quota.remaining}
        onRefined={(entry, q) => {
          setGallery((g) => [entry, ...g]);
          setCurrentId(entry.id);
          setActiveView(entry.view || "front");
          if (q) setQuota(q);
        }}
      />

      <CameraViewsDialog
        open={cameraViewsOpen}
        onOpenChange={setCameraViewsOpen}
        sourceId={displayed?.id || current?.id || currentFrontId}
        creditsRemaining={quota.remaining}
        onSubmit={onCameraViewsSubmit}
      />

      <RenderFromCadDialog
        open={renderFromCadOpen}
        onOpenChange={setRenderFromCadOpen}
        creditsRemaining={quota.remaining}
        onSubmit={onRenderFromCadSubmit}
      />

      <QuickRefineDialog
        open={!!quickRefineTarget}
        onOpenChange={(v) => !v && setQuickRefineTarget(null)}
        target={quickRefineTarget}
        cost={1}
        onSubmit={submitQuickRefine}
      />

      <SplitComponentDialog
        open={!!splitTarget}
        onOpenChange={(v) => !v && setSplitTarget(null)}
        target={splitTarget}
        onSubmit={submitSplitComponent}
      />

      <AutoSplitDialog
        open={!!autoSplitTarget}
        onOpenChange={(v) => !v && setAutoSplitTarget(null)}
        target={autoSplitTarget}
        onExtracted={onAutoSplitExtracted}
        onQuotaChange={setQuota}
      />

      <MaterialsDrawer
        open={!!materialsTarget}
        onOpenChange={(v) => !v && setMaterialsTarget(null)}
        render={materialsTarget}
        onSaved={(renderId, materials, source) => {
          setGallery((g) =>
            g.map((x) =>
              x.id === renderId
                ? { ...x, materials, materials_source: source }
                : x
            )
          );
        }}
      />
      {presetPickerTarget && (
        <MaterialPresetPicker
          target={presetPickerTarget}
          gallery={gallery}
          onClose={() => setPresetPickerTarget(null)}
          onApplied={(sourceId, materials) => {
            setGallery((g) =>
              g.map((x) =>
                x.id === presetPickerTarget.id
                  ? { ...x, materials, materials_source: "preset", materials_preset_from: sourceId }
                  : x
              )
            );
          }}
        />
      )}

      <SaveToPortfolioDialog
        open={!!savePortfolioTarget}
        onOpenChange={(v) => !v && setSavePortfolioTarget(null)}
        source={savePortfolioTarget}
      />

      <RedeemCodeDialog
        open={redeemOpen}
        onOpenChange={setRedeemOpen}
        onRedeemed={refreshWallet}
      />

      <PackPickerDialog
        open={packPickerOpen}
        onOpenChange={setPackPickerOpen}
        packs={packs}
        onSelect={(p) => {
          setPackPickerOpen(false);
          setBuyPack(p);
        }}
        onSelectCustom={() => {
          setPackPickerOpen(false);
          setCustomTopUpOpen(true);
        }}
      />

      <CustomTopUpDialog
        open={customTopUpOpen}
        onOpenChange={setCustomTopUpOpen}
        onPaid={refreshWallet}
      />

      <CheckoutDialog
        open={!!buyPack}
        onOpenChange={(v) => !v && setBuyPack(null)}
        pack={buyPack}
        onPaid={refreshWallet}
      />

      <CadDialog
        open={cadDialogOpen}
        onOpenChange={setCadDialogOpen}
        displayed={displayed}
        gallery={gallery}
        onOpenMaterials={setMaterialsTarget}
        quota={quota}
        onCreditsChanged={refreshWallet}
        onRequestCadMultiview={onRequestCadMultiview}
        onMultiviewRefine={onMultiviewRefine}
      />
    </div>
  );
}
